import { Box, Flex } from "@chakra-ui/layout";
import { ReactFlowProvider } from "@xyflow/react";
import EntityGraph from "features/entities/EntityCreatePage/EntityGraph/EntityGraph";
import { GraphRef } from "features/entities/EntityCreatePage/EntityGraph/types";
import FormSidebar from "features/entities/EntityCreatePage/FormSidebar/FormSidebar";
import { makeEmptyShareClass } from "features/entities/EntityCreatePage/FormSidebar/ShareClasses";
import { useFundEntityWithAllocationRulesCreate } from "gen/endpoints/entity-with-allocation-rule-edges/entity-with-allocation-rule-edges";
import { FundEntityAndAllocationRuleEdgesToAddRequest } from "gen/model/fundEntityAndAllocationRuleEdgesToAddRequest";
import { useRef } from "react";
import { Control, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import useDocumentTitle from "shared/hooks/useDocumentTitle";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export type FormValues = Omit<
  FundEntityAndAllocationRuleEdgesToAddRequest,
  "investing_into_entities"
> & {
  investing_into_entities: string[];
};

export type FormControl = Control<FormValues>;

export const EntityCreatePage: React.FC = () => {
  const { t } = useTranslation(["address", "common", "entity"]);
  useDocumentTitle(t("common:entities"));

  const form = useForm<FormValues>({
    defaultValues: {
      entity_data: {},
      investing_into_entities: [],
      security_class_base_data: [makeEmptyShareClass()],
    },
  });

  const ref = useRef<GraphRef>(null);
  const navigate = useNavigate();
  const { fundFamilyId } = useRequiredStringParams();

  const { mutateAsync: createFundEntityWithAllocationRules, isPending } =
    useFundEntityWithAllocationRulesCreate();

  const onSubmit = async (data: FormValues) => {
    await createFundEntityWithAllocationRules({
      fundFamilyId,
      data: {
        ...data,
        investing_into_entities: data.investing_into_entities.map((id) => ({
          entity_id: id,
          security_class_id: null,
        })),
        entity_data: {
          ...data.entity_data,
          fund_family_id: fundFamilyId,
        },
      },
    });
    const entitiesRoute = rootRoute({})
      .fundFamilies({})
      .fundFamily({ fundFamilyId })
      .setup({})
      .entities({}).$;
    navigate(entitiesRoute);
  };

  return (
    <PageContent isFullHeight={true} contentWidth="full-width">
      <ReactFlowProvider>
        <Form form={form} onSubmit={onSubmit}>
          <PageBody>
            <Flex height="100%">
              <Box
                borderRightColor="gray.200"
                borderRightStyle="solid"
                borderRightWidth="1px"
                flex={1}
                minWidth="500px"
                id="it-form-sidebar"
              >
                <FormSidebar isLoading={isPending} />
              </Box>
              <Box flex={2} id="it-graph-container" height="100%">
                <EntityGraph fundFamilyId={fundFamilyId} ref={ref} />
              </Box>
            </Flex>
          </PageBody>
        </Form>
      </ReactFlowProvider>
    </PageContent>
  );
};
